import { Badge, Box, Flex, IconButton, Text, Tooltip } from '@chakra-ui/react';
import { ChatIcon, CohortIcon, CopyIcon, EditIcon, TimeIcon, VideoIcon } from '@udacity/chakra-uds-icons';
import { useAppToast } from 'app/common/hooks/use-app-toast';
import { commonLanguages } from 'app/helpers/form-helper';
import { dateFormatter } from 'app/helpers/format-helper';
import { copyToClipboard } from 'app/helpers/string-helper';
import { FetchedMeeting } from 'app/uconnect/helpers/types';
import { getExcuseRequestCount } from 'app/uconnect/helpers/utils';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

type Props = {
  meeting: FetchedMeeting;
};

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text color="gray.600" fontSize="xs" fontWeight={600} textTransform="uppercase" letterSpacing="wider">
    {children}
  </Text>
);

export const MeetingCard: React.FC<Props> = ({ meeting }) => {
  const { t } = useTranslation();
  const toast = useAppToast();

  const handleCopyLink = async () => {
    if (!meeting.conferenceLink) return;
    const copied = await copyToClipboard(meeting.conferenceLink);
    toast({
      variant: copied ? 'success' : 'error',
      description: copied ? t('connect.linkCopied') : t('error.anErrorOccurred'),
    });
  };

  const languages = meeting.locales.map(
    (locale) =>
      commonLanguages.find((lang) => locale === lang.code) || {
        name: 'English',
      }
  );

  const startTime = dateFormatter.localTime(meeting.startAt);
  const endTime = dateFormatter.localTime(meeting.endAt);
  const excuseRequestCount = getExcuseRequestCount(meeting);

  return (
    <Flex direction="column" gap={4} py={2} position="relative">
      <Flex position="absolute" top={0} right={0} gap={2} zIndex={1}>
        {meeting.conferenceLink && (
          <>
            <Tooltip label={t('connect.joinMeeting')}>
              <IconButton
                as="a"
                href={meeting.conferenceLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('connect.joinMeeting')}
                colorScheme="blue"
                size="sm"
                isRound
                boxSize={9}
                minW={9}
                p={0}
                icon={<VideoIcon h={5} w={5} />}
              />
            </Tooltip>
            <Tooltip label={t('connect.copyUrl')}>
              <IconButton
                aria-label={t('connect.copyUrl')}
                colorScheme="blue"
                size="sm"
                isRound
                boxSize={9}
                minW={9}
                p={0}
                icon={<CopyIcon h={5} w={5} />}
                onClick={handleCopyLink}
              />
            </Tooltip>
          </>
        )}
        <Tooltip label={t('connect.editAttendance')}>
          <IconButton
            as={RouterLink}
            to={`/connect/meetings/${meeting.id}`}
            aria-label={t('connect.editAttendance')}
            colorScheme="blue"
            size="sm"
            isRound
            boxSize={9}
            minW={9}
            p={0}
            icon={<EditIcon h={5} w={5} />}
          />
        </Tooltip>
      </Flex>
      {meeting.series?.name && (
        <Box>
          <FieldLabel>{t('connect.seriesLabel')}</FieldLabel>
          <Text>{meeting.series.name}</Text>
        </Box>
      )}
      <Box>
        <FieldLabel>{t('connect.sessionLabel')}</FieldLabel>
        <Text>{meeting.name}</Text>
      </Box>
      <Flex gap={2}>
        <Badge>
          <TimeIcon h={4} mb={0.5} w={4} /> {startTime} - {endTime}
        </Badge>
        {languages.map((language) => (
          <Badge key={language.name}>
            <ChatIcon h={4} mb={0.5} w={4} /> {language.name}
          </Badge>
        ))}
        {meeting.meetingUsers.totalCount > 0 && (
          <Badge>
            <CohortIcon h={4} mb={0.5} w={4} />{' '}
            {t('connect.meetingUserCount', {
              count: meeting.meetingUsers.totalCount,
            })}
          </Badge>
        )}
        {excuseRequestCount > 0 && (
          <Badge bgColor="red.700" color="white">
            <CohortIcon h={4} mb={0.5} w={4} />{' '}
            {t('connect.excuseRequestCount', {
              count: excuseRequestCount,
            })}
          </Badge>
        )}
      </Flex>
      {meeting.instructions && (
        <Box>
          <FieldLabel>{t('connect.instructionsLabel')}</FieldLabel>
          <Text whiteSpace="pre-line">{meeting.instructions}</Text>
        </Box>
      )}
    </Flex>
  );
};
